import CloseIcon from '@mui/icons-material/Close';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import {
  buildDiscardActionFormConfirmation,
  useTrackActionFormDirty,
} from 'components/ActionDialog/actionFormDirtyState';
import AlertDialog from 'components/atoms/Dialog/AlertDialog';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import {
  AnnotationAssignment,
  AnnotationAssignmentUpdate,
  AnnotationClassRemovalOperation,
  ClassRemovalAction,
  TodoOption,
} from 'interfaces/annotation';
import {
  concat,
  filter,
  find,
  findIndex,
  first,
  flatMap,
  gt,
  includes,
  isArray,
  isEmpty,
  map,
  reduce,
  size,
  slice,
  some,
  sortBy,
  toLower,
  trim,
  uniq,
} from 'lodash';
import React, { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { getTodoClassKey } from 'services/annotations/helpers';
import useHandleUnload from 'utils/useHandleUnload';
import * as yup from 'yup';
import { DEFAULT_TODO, DEFAULT_TODO_TYPE } from './CreateAnnotationAssignmentDialog';
import CreateAnnotationAssignmentSection from './CreateAnnotationAssignmentSection';
import { DeleteClassFromAssignmentDialog, options, replaceClassAction } from './DeleteClassesFromAssignmentDialog';
import {
  classifyTodoOptions,
  deduplicateTodoOptionsByName,
  getDuplicateNameKeySet,
  getTodoOptionNameKey,
  getUnconfiguredAnnotationValues,
  parseTodoOptionRowId,
  UnconfiguredAnnotationValue,
} from './annotationClassOptions';
import { useGetClassToAnnotate } from './useGetClassToAnnotateFromTaxonomyId';

export interface AssignmentFormValues extends AnnotationAssignmentUpdate {}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  buttonText: string;
  isLoadingButtonText: string;
  currentAssignment?: AnnotationAssignment;
  handleApply: SubmitHandler<AssignmentFormValues>;
  isLoading?: boolean;
  classesToRemoveWithActions?: AnnotationClassRemovalOperation[];
  setClassesToRemoveWithActions?: React.Dispatch<React.SetStateAction<AnnotationClassRemovalOperation[]>>;
  onDeleteDuplicateClasses?: (classNames: string[]) => Promise<void>;
  isDeletingDuplicateClasses?: boolean;
}

const AnnotationAssignmentDialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  buttonText,
  isLoadingButtonText,
  currentAssignment,
  handleApply,
  isLoading,
  classesToRemoveWithActions,
  setClassesToRemoveWithActions,
  onDeleteDuplicateClasses,
  isDeletingDuplicateClasses,
}) => {
  const confirmWithModal = useConfirmation();

  const initialClassOptions = useMemo(
    () => classifyTodoOptions(first(currentAssignment?.todos)?.options || []),
    [currentAssignment?.todos]
  );
  const [cleanedDuplicateClassNames, setCleanedDuplicateClassNames] = React.useState<string[]>([]);
  const [skippedDuplicateClassNames, setSkippedDuplicateClassNames] = React.useState<string[]>([]);
  const duplicateClassGroups = filter(
    initialClassOptions.duplicateGroups,
    (group) => !includes(cleanedDuplicateClassNames, group.nameKey)
  );
  const todoWithActiveOptions = {
    ...first(currentAssignment?.todos),
    options: initialClassOptions.activeOptions,
  };

  const assignmentCreationForm = useForm<AssignmentFormValues>({
    mode: 'onChange',
    resolver: yupResolver(assignmentCreationSchema),
    defaultValues: currentAssignment
      ? {
          name: currentAssignment?.name,
          isGlobal: currentAssignment?.isGlobal,
          todos: [todoWithActiveOptions],
        }
      : {
          name: '',
          isGlobal: false,
          todos: [
            {
              todo: DEFAULT_TODO,
              type: DEFAULT_TODO_TYPE,
              options: [],
            },
          ],
        },
  });

  const {
    watch,
    formState: { isDirty },
  } = assignmentCreationForm;
  const hasChanges = isDirty;
  useTrackActionFormDirty(hasChanges);

  const { getTodoOptionFromClassId } = useGetClassToAnnotate();

  // Optimistic client-side view of records the user has explicitly, permanently deleted via
  // the duplicate cleanup action in this session (the server mutation already ran; this just
  // keeps the open dialog visually consistent until the assignment prop refetches).
  const [manuallyRemovedDuplicateOptions, setManuallyRemovedDuplicateOptions] = React.useState<TodoOption[]>([]);

  const alreadyDeletedOptions = useMemo(
    () => filter(initialClassOptions.archivedOptions, (option) => !includes(manuallyRemovedDuplicateOptions, option)),
    [initialClassOptions.archivedOptions, manuallyRemovedDuplicateOptions]
  );

  const duplicateNameKeySet = useMemo(
    () => getDuplicateNameKeySet([...(first(watch('todos'))?.options ?? []), ...alreadyDeletedOptions]),
    [watch('todos'), alreadyDeletedOptions]
  );

  const unconfiguredAnnotationValues = useMemo(
    () =>
      getUnconfiguredAnnotationValues({
        annotations: currentAssignment?.annotations ?? [],
        configuredOptions: [
          ...(first(currentAssignment?.todos)?.options ?? []),
          ...(first(watch('todos'))?.options ?? []),
        ],
        resolveOption: getTodoOptionFromClassId,
      }),
    [currentAssignment?.annotations, currentAssignment?.todos, watch('todos'), getTodoOptionFromClassId]
  );

  const [openRemoveClassDialog, setOpenRemoveClassDialog] = React.useState(false);
  const [alertDialogTitle, setAlertDialogTitle] = React.useState('');
  const [selectedClassToRemove, setSelectedClassToRemove] = React.useState<TodoOption | null>(null);
  const [deleteClassFromAssignmentDialogOptions, setDeleteClassFromAssignmentDialogOptions] = React.useState(options);

  const handleAlertDialogClose = () => {
    setAlertDialogTitle('');
  };

  const isExistingAnnotations = (classToCheck: TodoOption) => {
    const currentClassToCheckClassKey = getTodoClassKey(classToCheck);
    return some(
      currentAssignment?.annotations,
      (slideAnnotations) =>
        slideAnnotations.annotationSummary?.[currentClassToCheckClassKey] ||
        slideAnnotations.draftAnnotationSummary?.[currentClassToCheckClassKey]
    );
  };

  const handleDeleteClassToAnnotate = (rowId: string) => {
    const { index } = parseTodoOptionRowId(rowId);
    const previousClassesToAnnotate = first(assignmentCreationForm.watch('todos'))?.options || [];
    const currentClassToRemove = previousClassesToAnnotate[index];
    if (!currentClassToRemove) {
      return;
    }

    const classesToRemoveWithReplaceAction = filter(
      classesToRemoveWithActions,
      (classToRemoveWithAction) => classToRemoveWithAction.action === ClassRemovalAction.replace
    );

    if (includes(map(classesToRemoveWithReplaceAction, 'classToReplaceWith'), currentClassToRemove)) {
      setAlertDialogTitle('This class cannot be deleted');
      return;
    }

    setSelectedClassToRemove(currentClassToRemove);

    // only if there are annotations with this class, we need to show the dialog
    if (isExistingAnnotations(currentClassToRemove)) {
      setDeleteClassFromAssignmentDialogOptions(options);
      setOpenRemoveClassDialog(true);
    } else {
      handleRemoveClassFromAssignment(currentClassToRemove, ClassRemovalAction.keep);
    }
  };

  const handleReplaceClassToAnnotate = (rowId: string) => {
    const { index } = parseTodoOptionRowId(rowId);
    const previousClassesToAnnotate = first(assignmentCreationForm.watch('todos'))?.options || [];
    const currentClassToReplace = previousClassesToAnnotate[index];
    if (!currentClassToReplace) {
      return;
    }

    const classesToRemoveWithReplaceAction = filter(
      classesToRemoveWithActions,
      (classToRemoveWithAction) => classToRemoveWithAction.action === ClassRemovalAction.replace
    );

    if (includes(map(classesToRemoveWithReplaceAction, 'classToReplaceWith'), currentClassToReplace)) {
      setAlertDialogTitle('This class cannot be replaced');
      return;
    }

    setDeleteClassFromAssignmentDialogOptions([replaceClassAction]);
    setSelectedClassToRemove(currentClassToReplace);
    setOpenRemoveClassDialog(true);
  };

  const removeClassFromAssignmentTodo = (classToRemove: TodoOption) => {
    if (!classToRemove) return;

    const prevTodos = assignmentCreationForm.watch('todos');
    const previousClassesToAnnotate = first(prevTodos)?.options || [];
    assignmentCreationForm.setValue(
      'todos',
      [
        {
          ...first(prevTodos),
          options: filter(
            previousClassesToAnnotate,
            (previousClassToAnnotate) => previousClassToAnnotate !== classToRemove
          ),
        },
      ],
      { shouldDirty: true }
    );
  };

  const handleRemoveClassFromAssignment = (
    classToRemove: TodoOption,
    action: ClassRemovalAction,
    classToReplaceWith?: TodoOption
  ) => {
    // for assignment creation, we don't need to update any annotations
    if (setClassesToRemoveWithActions) {
      setClassesToRemoveWithActions((prevClasses) => [
        // if we are editing a removed class action, we need to remove the previous action
        ...filter(prevClasses, (currentClassToRemove) => currentClassToRemove.classToRemove !== classToRemove),
        { classToRemove, action, classToReplaceWith },
      ]);
      setOpenRemoveClassDialog(false);
    }

    removeClassFromAssignmentTodo(classToRemove);
  };

  const handleEditDeletedClass = (rowId: string) => {
    if (!setClassesToRemoveWithActions) {
      return;
    }

    const { segment, index } = parseTodoOptionRowId(rowId);
    if (segment !== 'removalPending') {
      return;
    }

    const currentClassToRemoveEdit = classesToRemoveWithActions?.[index];
    if (currentClassToRemoveEdit) {
      setSelectedClassToRemove(currentClassToRemoveEdit.classToRemove);
      setOpenRemoveClassDialog(true);
    }
  };

  const handleRestoreDeletedClass = (rowId: string) => {
    const { segment, index } = parseTodoOptionRowId(rowId);

    if (segment === 'archived') {
      const deletedOptionUndo = alreadyDeletedOptions[index];
      if (!deletedOptionUndo) {
        return;
      }

      const prevTodos = assignmentCreationForm.watch('todos');
      const previousClassesToAnnotate = first(prevTodos)?.options || [];
      assignmentCreationForm.setValue(
        'todos',
        [
          {
            ...first(prevTodos),
            options: [...previousClassesToAnnotate, { ...deletedOptionUndo, deletedAt: undefined }],
          },
        ],
        { shouldDirty: true }
      );

      return;
    }

    if (setClassesToRemoveWithActions) {
      const previousClassToAnnotate = classesToRemoveWithActions?.[index]?.classToRemove;
      const originalIndex = previousClassToAnnotate
        ? findIndex(currentAssignment.todos[0].options, (option) => option === previousClassToAnnotate)
        : -1;

      if (previousClassToAnnotate && originalIndex !== -1) {
        const currentTodos = assignmentCreationForm.watch('todos');
        const currentClassesToAnnotate = first(currentTodos)?.options || [];
        assignmentCreationForm.setValue(
          'todos',
          [
            {
              ...first(currentTodos),
              options: [
                ...slice(currentClassesToAnnotate, 0, originalIndex),
                previousClassToAnnotate,
                ...slice(currentClassesToAnnotate, originalIndex),
              ],
            },
          ],
          {
            shouldDirty: true,
          }
        );
      }
      setClassesToRemoveWithActions((prevClasses) => filter(prevClasses, (_, entryIndex) => entryIndex !== index));
    }
  };

  const handleColorChange = (rowId: string, color: any) => {
    const { index } = parseTodoOptionRowId(rowId);
    const prevTodos = assignmentCreationForm.watch('todos');
    const previousClassesToAnnotate = first(prevTodos)?.options || [];
    assignmentCreationForm.setValue(
      'todos',
      [
        {
          ...first(prevTodos),
          options: map(previousClassesToAnnotate, (previousClassToAnnotate, optionIndex) =>
            optionIndex === index ? { ...previousClassToAnnotate, color } : previousClassToAnnotate
          ),
        },
      ],
      { shouldDirty: true }
    );
  };

  // The user can arrange the classes in any order they prefer.
  // However, when new classes are added, they should be inserted in alphabetical order
  // while retaining the order of the existing classes.
  const combineRetainedAndAddedClasses = (retained: TodoOption[], added: TodoOption[]): TodoOption[] => {
    const getSortLabelBy = (todo: TodoOption) => trim(todo.displayName ?? todo.name ?? '');
    const getSortKey = (todo: TodoOption) => toLower(getSortLabelBy(todo));
    const sortedAdded = sortBy(added, getSortLabelBy);

    // Merge: keep retained order, insert newly added items alphabetically by displayName (fallback name)
    return reduce(
      sortedAdded,
      (acc: TodoOption[], addedItem: TodoOption) => {
        const addedKey = getSortKey(addedItem);
        const insertionIndex = findIndex(acc, (existing) => gt(getSortKey(existing), addedKey));

        if (insertionIndex === -1) {
          return concat(acc, [addedItem]);
        }

        return concat(slice(acc, 0, insertionIndex), [addedItem], slice(acc, insertionIndex));
      },
      retained
    );
  };

  const handleClassesToAnnotateChange = (newClassesToAnnotate: TodoOption[]) => {
    const prevTodos = assignmentCreationForm.watch('todos');
    const prevClassesToAnnotate = first(prevTodos)?.options || [];
    const prevIds = map(prevClassesToAnnotate, getTodoClassKey);
    const addedCandidates = filter(
      newClassesToAnnotate,
      (newClassToAnnotate) => !includes(prevIds, getTodoClassKey(newClassToAnnotate))
    );
    const retained = filter(newClassesToAnnotate, (newClassToAnnotate) =>
      includes(prevIds, getTodoClassKey(newClassToAnnotate))
    );
    const { options: uniqueClassesToAnnotate, skippedDuplicateNames } = deduplicateTodoOptionsByName([
      ...retained,
      ...addedCandidates,
    ]);
    const uniqueClassKeys = map(uniqueClassesToAnnotate, getTodoClassKey);
    const added = filter(uniqueClassesToAnnotate, (option) => !includes(prevIds, getTodoClassKey(option)));
    const uniqueRetained = filter(retained, (option) => includes(uniqueClassKeys, getTodoClassKey(option)));

    const newClassesToAnnotateWithPreviousOrder = combineRetainedAndAddedClasses(uniqueRetained, added);
    setSkippedDuplicateClassNames(skippedDuplicateNames);

    assignmentCreationForm.setValue(
      'todos',
      [{ ...first(prevTodos), options: newClassesToAnnotateWithPreviousOrder }],
      { shouldDirty: true }
    );
    // validate to update errors
    assignmentCreationForm.trigger('todos');
  };

  const handleAddUnconfiguredAnnotationValue = (value: UnconfiguredAnnotationValue) => {
    const currentOptions = first(assignmentCreationForm.watch('todos'))?.options ?? [];
    handleClassesToAnnotateChange([...currentOptions, { ...value.option, deletedAt: undefined }]);
  };

  const handleDeleteDuplicateClasses = async (classNames: string[]) => {
    if (!onDeleteDuplicateClasses) {
      return;
    }

    const selectedGroups = filter(duplicateClassGroups, (group) => includes(classNames, group.nameKey));
    const duplicateCount = reduce(selectedGroups, (count, group) => count + group.duplicateOptions.length, 0);
    const classLabels = map(selectedGroups, 'label').join(', ');
    const confirmed = await confirmWithModal({
      title: 'Permanently delete duplicate records?',
      text: `Delete ${duplicateCount} duplicate ${
        duplicateCount === 1 ? 'record' : 'records'
      } for ${classLabels}? The first class record will be kept and annotations will not change.`,
      confirmButtonProps: { title: 'Delete duplicates', color: 'warning' },
      cancelButtonProps: { title: 'Cancel' },
    });
    if (!confirmed) {
      return;
    }

    await onDeleteDuplicateClasses(classNames);

    // The active-form options are react-hook-form's cloned copies, not the same object
    // references as initialClassOptions (which is derived straight from the raw prop), so we
    // cannot match them by reference. Instead, mirror the backend's "keep first, drop the rest"
    // rule directly against the live form array for exactly the confirmed class names.
    const namesBeingCleaned = new Set(classNames);
    const seenNamesInActiveList = new Set<string>();
    const prevTodos = assignmentCreationForm.watch('todos');
    const previousClassesToAnnotate = first(prevTodos)?.options || [];
    const nextActiveOptions = filter(previousClassesToAnnotate, (option) => {
      const nameKey = getTodoOptionNameKey(option);
      if (!namesBeingCleaned.has(nameKey)) {
        return true;
      }
      if (seenNamesInActiveList.has(nameKey)) {
        return false;
      }
      seenNamesInActiveList.add(nameKey);
      return true;
    });
    assignmentCreationForm.setValue('todos', [{ ...first(prevTodos), options: nextActiveOptions }], {
      shouldDirty: false,
    });

    // Archived duplicates ARE the same object references as initialClassOptions (both derived
    // straight from the raw assignment prop with no form cloning in between), so reference
    // matching is safe here.
    const removedOptions = flatMap(selectedGroups, 'duplicateOptions');
    setManuallyRemovedDuplicateOptions((currentOptions) => [...currentOptions, ...removedOptions]);

    setCleanedDuplicateClassNames((currentNames) => uniq([...currentNames, ...classNames]));
  };

  const handleClose = (event: React.MouseEvent, reason: string) => {
    if (reason !== 'backdropClick') {
      handleCloseButtonClick();
    }
  };

  const handleCloseButtonClick = async () => {
    if (hasChanges) {
      if (await confirmWithModal(buildDiscardActionFormConfirmation(title))) {
        handleDiscardChanges();
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    assignmentCreationForm.reset();
    setSkippedDuplicateClassNames([]);
    if (setClassesToRemoveWithActions) {
      setClassesToRemoveWithActions([]);
    }
  };

  useHandleUnload({ showAlert: hasChanges });

  const selectedClassRemovalOperation = selectedClassToRemove
    ? find(classesToRemoveWithActions, (classToRemove) => classToRemove.classToRemove === selectedClassToRemove)
    : undefined;

  // if there is only one option, we can set it as default, otherwise, we set keep as default
  const defaultRemovalAction: ClassRemovalAction =
    size(deleteClassFromAssignmentDialogOptions) === 1
      ? first(deleteClassFromAssignmentDialogOptions)?.id ?? ClassRemovalAction.keep
      : ClassRemovalAction.keep;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDialogContent-root': {
            padding: 0,
          },
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          {title}
          <IconButton
            aria-label="close"
            onClick={handleCloseButtonClick}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <>
            <CreateAnnotationAssignmentSection
              assignmentCreationForm={assignmentCreationForm}
              onDeleteClassToAnnotate={handleDeleteClassToAnnotate}
              onReplaceClassToAnnotate={handleReplaceClassToAnnotate}
              isExistingAnnotations={(currentClassToEdit: TodoOption) => {
                return isExistingAnnotations(currentClassToEdit);
              }}
              onColorChange={handleColorChange}
              onClassesToAnnotateChange={handleClassesToAnnotateChange}
              classesToRemoveWithActions={classesToRemoveWithActions}
              onEditDeleted={handleEditDeletedClass}
              onRestoreDeleted={handleRestoreDeletedClass}
              alreadyDeletedOptions={alreadyDeletedOptions}
              duplicateNameKeys={duplicateNameKeySet}
              unconfiguredAnnotationValues={unconfiguredAnnotationValues}
              onAddUnconfiguredAnnotationValue={handleAddUnconfiguredAnnotationValue}
              duplicateClassGroups={duplicateClassGroups}
              onDeleteDuplicateClasses={handleDeleteDuplicateClasses}
              isDeletingDuplicateClasses={isDeletingDuplicateClasses}
              skippedDuplicateClassNames={skippedDuplicateClassNames}
              onDismissSkippedDuplicateClasses={() => setSkippedDuplicateClassNames([])}
            />
            {handleRemoveClassFromAssignment && openRemoveClassDialog && (
              <DeleteClassFromAssignmentDialog
                open={openRemoveClassDialog}
                onCancel={() => setOpenRemoveClassDialog(false)}
                removedTodoOption={selectedClassToRemove}
                assignment={{
                  ...currentAssignment,
                  todos: watch('todos'),
                }}
                selectedAction={selectedClassRemovalOperation?.action ?? defaultRemovalAction}
                selectedClassToReplaceWith={selectedClassRemovalOperation?.classToReplaceWith ?? null}
                onContinue={(action: ClassRemovalAction, classToReplaceWith?: TodoOption) =>
                  handleRemoveClassFromAssignment(selectedClassToRemove, action, classToReplaceWith)
                }
                classActionOptions={deleteClassFromAssignmentDialogOptions}
              />
            )}
          </>
        </DialogContent>
        <DialogActions>
          {hasChanges ? (
            <>
              <Button
                onClick={assignmentCreationForm.handleSubmit(handleApply)}
                color="primary"
                variant="contained"
                disabled={isLoading || !isEmpty(duplicateClassGroups)}
              >
                {isLoading ? isLoadingButtonText : buttonText}
              </Button>
              <Button onClick={handleDiscardChanges} color="warning" variant="contained" disabled={isLoading}>
                Discard changes
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseButtonClick} color="primary" variant="outlined">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <AlertDialog
        open={!isEmpty(alertDialogTitle)}
        onClose={handleAlertDialogClose}
        title={alertDialogTitle}
        message="This class is already selected to replace another class. Change it before you delete this class"
        severity="warning"
      />
    </>
  );
};

const assignmentCreationSchema = yup.object({
  name: yup.string().required('Assignment name is required'),
  todos: yup
    .array()
    .required()
    .min(1, 'At least one todo is required')
    .test(
      'has-options',
      'At least one class is required',
      (todos) =>
        isArray(todos) && todos.length > 0 && isArray(first(todos)?.options) && first(todos)?.options.length > 0
    ),
});

export default AnnotationAssignmentDialog;
